import { buildApiUrl } from '../config/api';
import { session } from './session';

class TokenManager {
  constructor() {
    this.currentToken = null;
    this.tokenExpiry = null;
    this.isGenerating = false;
    this.tokenSessionId = null; // Track which session ID generated the current token
    this.retryCount = 0;
    this.maxRetries = 3;
    this.refreshInterval = null;
    this.isInitialized = false;
  }

  /**
   * Get a valid security token, generating a new one if necessary
   */
  async getToken() {
    // Check if we have a valid token and if it was generated with the current session ID
    const currentSessionId = session.getSessionId();
    
    if (this.isTokenValid() && this.tokenSessionId === currentSessionId) {
      return this.currentToken;
    }

    // If session ID changed or token is invalid, clear the token
    if (this.tokenSessionId !== currentSessionId) {
      console.log('Session ID changed, clearing old token');
      this.clearToken();
    }

    // Generate a new token
    return await this.generateToken();
  }

  /**
   * Check if the current token is still valid
   */
  isTokenValid() {
    if (!this.currentToken || !this.tokenExpiry) {
      return false;
    }

    // Check if token has expired (with 2 minute buffer for better UX)
    const now = new Date();
    const expiryTime = new Date(this.tokenExpiry);
    const bufferTime = new Date(expiryTime.getTime() - 120000); // 2 minute buffer

    return now < bufferTime;
  }

  /**
   * Check if token needs refresh (within 10 minutes of expiry)
   */
  needsRefresh() {
    if (!this.currentToken || !this.tokenExpiry) {
      return true;
    }

    const now = new Date();
    const expiryTime = new Date(this.tokenExpiry);
    const refreshTime = new Date(expiryTime.getTime() - 600000); // 10 minutes before expiry

    return now >= refreshTime;
  }

  /**
   * Generate a new security token from the server
   */
  async generateToken() {
    if (this.isGenerating) {
      // Wait for the current generation to complete
      while (this.isGenerating) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.currentToken;
    }

    this.isGenerating = true;

    try {
      const currentSessionId = session.getSessionId();
      
      const response = await fetch(buildApiUrl('api/generate-token'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: currentSessionId
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.success) {
        this.currentToken = data.token;
        this.tokenExpiry = data.expires_at;
        this.tokenSessionId = currentSessionId; // Store the session ID that generated this token
        this.retryCount = 0; // Reset retry count on successful generation
        
        console.log('Generated new security token');
        
        // Start periodic refresh if not already started
        this.startPeriodicRefresh();
        
        return this.currentToken;
      } else {
        throw new Error(data.error || 'Failed to generate token');
      }
    } catch (error) {
      console.error('Error generating security token:', error);
      this.retryCount++;
      
      // If we've exceeded max retries, throw the error
      if (this.retryCount >= this.maxRetries) {
        this.retryCount = 0;
        throw error;
      }
      
      // Wait a bit before retrying
      await new Promise(resolve => setTimeout(resolve, 1000 * this.retryCount));
      
      // Retry token generation
      this.isGenerating = false;
      return await this.generateToken();
    } finally {
      this.isGenerating = false;
    }
  }

  /**
   * Start periodic token refresh
   */
  startPeriodicRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }

    // Check every 5 minutes if token needs refresh
    this.refreshInterval = setInterval(async () => {
      if (this.needsRefresh() && !this.isGenerating) {
        console.log('Token needs refresh, generating new token...');
        try {
          await this.generateToken();
        } catch (error) {
          console.error('Failed to refresh token:', error);
        }
      }
    }, 5 * 60 * 1000); // 5 minutes
  }

  /**
   * Stop periodic token refresh
   */
  stopPeriodicRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  /**
   * Get headers with security token for API requests
   */
  async getAuthHeaders() {
    const token = await this.getToken();
    return {
      'X-Security-Token': token,
      'X-Session-ID': session.getSessionId()
    };
  }

  /**
   * Clear the current token (useful for logout or errors)
   */
  clearToken() {
    this.currentToken = null;
    this.tokenExpiry = null;
    this.tokenSessionId = null; // Also clear the session ID
    this.retryCount = 0; // Reset retry count
    this.stopPeriodicRefresh();
  }

  /**
   * Handle token validation failure by clearing token and retrying
   */
  async handleTokenFailure() {
    console.log('Token validation failed, clearing token and retrying...');
    this.clearToken();
    return await this.getToken();
  }

  /**
   * Initialize token generation when the app starts
   */
  async initialize() {
    if (this.isInitialized) {
      return;
    }

    try {
      await this.generateToken();
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize security token:', error);
      // Clear any partial token data on initialization failure
      this.clearToken();
      // Don't throw here - the app should still work without tokens
      // Individual API calls will handle token generation as needed
    }
  }

  /**
   * Cleanup when the app is unmounting
   */
  cleanup() {
    this.stopPeriodicRefresh();
    this.isInitialized = false;
  }
}

// Create a singleton instance
const tokenManager = new TokenManager();

export default tokenManager; 